import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import React, {useContext, useEffect, useRef, useState} from "react";
import {FormHelperText, LinearProgress, Typography} from "@mui/material";
import {Check, CloudUpload, Error, LocalCafe, Warning} from "@mui/icons-material";
import useGeneralEditVideoForm from "./useGeneralEditVideoForm";
import {useStyles} from "./GeneralEditVideoForm.css";
import {useLocation} from "react-router-dom";
import Dropzone from "react-dropzone";
import {useSnackbar} from "notistack";
import {useIntl} from "react-intl";
import SuggestionTextField from "../../../../commons/elements/autocomplete/SuggestionTextField";
import useLoadVideoAutocompleteOptions from "./useLoadVideoAutocompleteOptions";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import hash from "object-hash";
import {Prompt} from 'react-router'

const GeneralEditVideoForm = ({
                                  selectedVideo,
                                  associatedScoredScenes,
                                  editedVideoRef,
                                  setSaveButtonEnabled
                              }) => {
    const classes = useStyles();
    const location = useLocation();

    // Get permission-based limited association structure / Checking if the edit/saving operations are allowed
    const {allowedAssociationStructure, isOperationOnObjectAllowed} = useContext(FirebaseAuthContext);


    // Hook providing various form control functions for edited the user
    const {
        association,
        discipline,
        tournament,
        year,
        ageGroup,
        gender,
        surnameAthlete1,
        surnameAthlete2,
        bout,
        videoFile,
        handleChangeAssociation,
        handleChangeDiscipline,
        handleChangeTournament,
        handleChangeYear,
        handleChangeAgeGroup,
        handleChangeGender,
        handleChangeSurnameAthlete1,
        handleChangeSurnameAthlete2,
        handleChangeBout,
        handleDropVideo
    } = useGeneralEditVideoForm(selectedVideo, editedVideoRef)

    // Video autocomplete options (tournament, athlete 1, athlete 2)
    const {fetchAutocompleteOptions} = useLoadVideoAutocompleteOptions()

    const {enqueueSnackbar} = useSnackbar();

    // Internationalization for simple strings
    const intl = useIntl();

    const [autocompleteOptions, setAutocompleteOptions] = useState({})

    // Handling for hinting the user to save changes in case he tries to leave the form after making changes
    const initialHash = useRef(null)
    const [hasFormChanged, setHasFormChanged] = useState(false)

    // Fetch the autocomplete options based on the selected association (on each association-change)
    useEffect(() => {
        if (association) {
            fetchAutocompleteOptions(association)
                .then((options) => setAutocompleteOptions(options))
        }
    }, [association])

    useEffect(() => {
        if (location.pathname.match('/admin/videos/.*/edit')) {
            setSaveButtonEnabled(isOperationOnObjectAllowed(selectedVideo) && association && discipline && tournament && year && ageGroup && surnameAthlete1 && surnameAthlete2 && gender && bout && surnameAthlete1 !== surnameAthlete2 && scoreDefinitionMatchingWithSelectedDiscipline())
        }
        if (location.pathname === '/admin/videos/new') {
            setSaveButtonEnabled(association && discipline && tournament && year && ageGroup && surnameAthlete1 && surnameAthlete2 && gender && bout && videoFile && surnameAthlete1 !== surnameAthlete2)
        }

        // Refresh the current hash and set the initial hash at the very beginning
        const currentHash = hash(editedVideoRef.current, {unorderedArrays: true})
        if (!initialHash.current) {
            initialHash.current = currentHash
        }
        setHasFormChanged(initialHash.current !== currentHash)

    }, [association, discipline, tournament, year, ageGroup, surnameAthlete1, surnameAthlete2, gender, bout, videoFile, associatedScoredScenes])

    const showIfTrue = (text, condition) => {
        if (condition) {
            return text
        }
        return ''
    }

    const getDropzoneClass = (isDragAccept, isDragReject) => {
        if (isDragAccept) {
            return classes.dropzoneAccept
        }
        if (isDragReject) {
            return classes.dropzoneReject
        }
        return classes.dropzoneInactive
    }

    const getDragInfo = (isDragAccept, isDragReject) => {
        if (isDragAccept) {
            return <div>
                <Check className={classes.dragIcon}/>
                <p>
                    {intl.formatMessage({
                        id: 'app.videos.edit.form.dropInfo.format.valid',
                        defaultMessage: 'Valid video format'
                    })}
                </p>
            </div>
        }
        if (isDragReject) {
            return <div>
                <Error className={classes.dragIcon}/>
                <p>
                    {intl.formatMessage({
                        id: 'app.videos.edit.form.dropInfo.format.invalid',
                        defaultMessage: 'Invalid video format'
                    })}
                </p>
            </div>
        }
        if (videoFile) {
            return <div>
                <Check className={classes.dragIcon}/>
                <p>
                    {intl.formatMessage({
                        id: 'app.videos.edit.form.dropInfo.video.ready',
                        defaultMessage: 'Video ready to be uploaded'
                    })}
                </p>
            </div>
        }
        return <div>
            <CloudUpload className={classes.dragIcon}/>
            <p>
                {intl.formatMessage({
                    id: 'app.videos.edit.form.dropInfo.video.instruction',
                    defaultMessage: 'Drop video or click to select a video'
                })}
            </p>
        </div>
    }

    const getFileInfo = () => {
        return <div>
            <em>
                <b>
                    <span className={classes.formControl}>
                        {videoFile && intl.formatMessage({
                                id: 'app.videos.edit.form.dropInfo.video.filename',
                                defaultMessage: 'Filename: {filename}'
                            },
                            {filename: videoFile.path})}
                    </span>
                </b>
            </em>
            <em>
                <b>
                    <span className={classes.formControl}>
                        {videoFile && intl.formatMessage({
                                id: 'app.videos.edit.form.dropInfo.video.size',
                                defaultMessage: 'Size: {filesize}'
                            },
                            {filesize: Math.round(videoFile.size / 1000000 * 100) / 100 + ' MB'})}
                    </span>
                </b>
            </em>
        </div>
    }

    const onDropVideo = async (acceptedFiles) => {
        if (acceptedFiles && acceptedFiles.length === 1) {
            handleDropVideo(acceptedFiles)
        } else {
            enqueueSnackbar(
                intl.formatMessage({
                    id: 'app.videos.messages.drop.error',
                    defaultMessage: 'The dropped video is not valid or too many files have been dropped.'
                }), {
                    variant: 'error',
                    preventDuplicate: true,
                    autoHideDuration: 5000
                });
        }
    }

    const scoreDefinitionMatchingWithSelectedDiscipline = () => {
        /* Return true in case no scoredScenes are created yet for this video or no discipline has been selected (yet)
           Both "association" and "discipline" can be assumed to be not undefined after this check */

        if (!associatedScoredScenes || !discipline) {
            return true
        }

        let valid = true
        const disciplineStructure = allowedAssociationStructure[association].disciplines.find((d) => d.name === discipline)

        associatedScoredScenes.forEach((scoredScene) => {

            // Check validity according to the amount of score definitions in the scored scene
            // For point stop disciplines only one definition and an optional concurrent definition is allowed
            // Ignore concurrent scores, which are possibly existing for point stop disciplines for the length check
            if (disciplineStructure.mode === 'point_stop' && scoredScene.scoreDefinitions.filter((sd) => !sd.concurrent).length > 1) {
                valid = false
            }

            // Running time disciplines must not have concurrent scores in their scored scenes
            if (disciplineStructure.mode === 'running_time' && scoredScene.scoreDefinitions.find((sd) => sd.concurrent)) {
                valid = false
            }

            // Check the validity according ot the defined scores of the discipline
            scoredScene.scoreDefinitions.forEach((scoreDefinition) => {
                if (!disciplineStructure.validScores.find((s) => s.id === scoreDefinition.score)) {
                    valid = false
                }
            })
        })
        return valid
    }

    const getYearsSince1990 = () => {
        const currentYear = new Date().getFullYear()
        const list = []
        for (let y = currentYear; y >= 1990; y--) {
            list.push(y)
        }
        return list
    }

    const getGenders = () => {
        return [
            'm', 'f'
        ]
    }

    const scoringRelevantDataChanged = () => {
        return associatedScoredScenes?.length > 0 &&
            !(selectedVideo?.basics.association === association
                && selectedVideo?.basics.discipline === discipline
                && selectedVideo?.basics.ageGroup === ageGroup)
    }

    const getClassInBlurMode = (classWithoutBlur, condition) => {
        if (condition) {
            return `${classWithoutBlur} ${classes.blur}`
        } else {
            return classWithoutBlur
        }
    }

    return (
        <fieldset disabled={!isOperationOnObjectAllowed(selectedVideo)} style={{borderWidth: 0}}>
            {/* Security prompt if the user tries to (maybe unintendendly) leave the page.
            This needs to be effective if there are changes made to the current object. */}
            <Prompt
                when={hasFormChanged}
                message={intl.formatMessage({
                    id: 'app.utils.unsavedChanges.hint',
                    defaultMessage: 'Your changes are not saved, are you sure you want to leave?'
                })}
            />
            <div className={classes.row}>
                {
                    /* Show the thumbnail in case the video is already uploaded (if comes from edit button in overview) */
                    selectedVideo && <div className={classes.column}>
                        {
                            /* Show thumbnail if available */
                            selectedVideo?.vimeo?.transcoded && selectedVideo.vimeo?.thumbnails
                                ? <img className={classes.thumbnail}
                                       src={selectedVideo.vimeo.firstScoreThumbnails?.size640 || selectedVideo.vimeo.thumbnails?.size640}
                                       alt="Big video thumbnail"/>
                                : <div className={classes.thumbnailTranscoding}>
                                    <Typography component="h1" variant="h5" color="inherit">
                                        {intl.formatMessage({
                                            id: 'app.videos.edit.form.transcodingInProgress.info',
                                            defaultMessage: 'Video optimization in progress...'
                                        })}
                                    </Typography>
                                    <p>
                                        {intl.formatMessage({
                                            id: 'app.videos.edit.form.transcodingInProgress.description1',
                                            defaultMessage: 'This might take a few minutes.'
                                        })}
                                    </p>
                                    <LocalCafe className={classes.dragIcon}/>
                                    <p>
                                        {intl.formatMessage({
                                            id: 'app.videos.edit.form.transcodingInProgress.description2',
                                            defaultMessage: 'Grab a cup of coffee :)'
                                        })}
                                    </p>
                                    <LinearProgress className={classes.transcodingProgress}/>
                                </div>
                        }
                    </div>
                }
                {<div className={classes.column}>
                    {/* File dropzone */}
                    <Dropzone
                        onDrop={onDropVideo}
                        multiple={false}
                        accept="video/*"
                        minSize={1024}
                        maxSize={3000000000}>
                        {({getRootProps, getInputProps, isDragAccept, isDragReject}) => {
                            return (
                                <div {...getRootProps({
                                    className: getDropzoneClass(isDragAccept, isDragReject)
                                })}>
                                    <input {...getInputProps()} />
                                    {getDragInfo(isDragAccept, isDragReject)}
                                    {getFileInfo()}
                                </div>
                            );
                        }}
                    </Dropzone>

                    {/* Brannmanndan video hint */}
                    <div className={classes.downloadInfoContainer}>
                        {intl.formatMessage({
                            id: 'app.videos.edit.form.videoSource.hint',
                            defaultMessage: 'Please only upload videos from the YouTube channel of <linkChannel>Brannmanndan</linkChannel> or self-recorded videos. We suggest the following YouTube downloader: <linkDownloader>SafeFrom.net</linkDownloader>.'
                        }, {
                            linkChannel: (component) => <a
                                href="https://www.youtube.com/user/Brannmanndan"
                                target="_blank" rel="noopener noreferrer"
                                style={{color: "white"}}>{component}</a>,
                            linkDownloader: (component) => <a
                                href="https://de.savefrom.net"
                                target="_blank" rel="noopener noreferrer"
                                style={{color: "white"}}>{component}</a>
                        })}
                    </div>
                </div>}
                <div className={classes.column}>

                    <div className={classes.row}>
                        {/* Association */}
                        <FormControl className={classes.formControl}>
                            <InputLabel className={classes.dropdownLabel} variant="outlined" required
                                        id="reg-video-association-label">
                                {intl.formatMessage({
                                    id: 'app.videos.edit.form.association.label',
                                    defaultMessage: 'Association'
                                })}
                            </InputLabel>
                            <Select id="reg-video-association-input" labelId="reg-video-association-label"
                                    variant="outlined"
                                    aria-describedby="reg-video-association-help"
                                    value={association} onChange={handleChangeAssociation}>
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>

                                {Object.keys(allowedAssociationStructure).map((association) =>
                                    <MenuItem key={association} value={association}>
                                        {intl.formatMessage({
                                            id: `app.ids.association.${association}`
                                        })}
                                    </MenuItem>
                                )}

                            </Select>
                            <FormHelperText variant="outlined" error={!association}
                                            id="reg-video-association-help">
                                {showIfTrue(intl.formatMessage({
                                    id: 'app.videos.edit.form.association.help',
                                    defaultMessage: 'Enter association'
                                }), !association)}
                            </FormHelperText>
                        </FormControl>

                        {/* Discipline */}
                        <FormControl className={getClassInBlurMode(classes.formControl, !association)}>
                            <InputLabel className={classes.dropdownLabel} variant="outlined" required
                                        id="reg-video-discipline-label">
                                {intl.formatMessage({
                                    id: 'app.videos.edit.form.discipline.label',
                                    defaultMessage: 'Discipline'
                                })}
                            </InputLabel>
                            <Select id="reg-video-discipline-input" labelId="reg-video-discipline-label"
                                    aria-describedby="reg-video-discipline-help"
                                    variant="outlined"
                                    value={discipline} onChange={handleChangeDiscipline}
                                    disabled={!association}>
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {allowedAssociationStructure[association]?.disciplines.map((item) => {
                                    return <MenuItem key={item.name} value={item.name}>
                                        {intl.formatMessage({
                                            id: `app.ids.discipline.${item.name}`
                                        })}
                                    </MenuItem>
                                })}
                            </Select>
                            <FormHelperText variant="outlined" error={!discipline} id="reg-video-discipline-help">
                                {showIfTrue(intl.formatMessage({
                                    id: 'app.videos.edit.form.discipline.help',
                                    defaultMessage: 'Enter discipline'
                                }), !discipline)}
                            </FormHelperText>
                        </FormControl>

                        {/* Bout */}
                        <FormControl className={getClassInBlurMode(classes.formControl, !association)}>
                            <InputLabel className={classes.dropdownLabel} variant="outlined" required
                                        id="reg-video-bout-label">
                                {intl.formatMessage({
                                    id: 'app.videos.edit.form.bout.label',
                                    defaultMessage: 'Bout'
                                })}
                            </InputLabel>
                            <Select id="reg-video-bout-input" labelId="reg-video-bout-label"
                                    aria-describedby="reg-video-bout-help"
                                    variant="outlined"
                                    value={bout} onChange={handleChangeBout}
                                    disabled={!association}>
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {allowedAssociationStructure[association]?.bouts.map((item) => {
                                    return <MenuItem key={item} value={item}>
                                        {intl.formatMessage({
                                            id: `app.ids.bout.${item}`
                                        })}
                                    </MenuItem>
                                })}
                            </Select>
                            <FormHelperText variant="outlined" error={!bout} id="reg-video-bout-help">
                                {showIfTrue(intl.formatMessage({
                                    id: 'app.videos.edit.form.bout.help',
                                    defaultMessage: 'Enter bout'
                                }), !bout)}
                            </FormHelperText>
                        </FormControl>
                    </div>

                    <div className={classes.row}>
                        {/* Tournament */}
                        <FormControl className={getClassInBlurMode(classes.formControl, !association)}>
                            <SuggestionTextField
                                disabled={!association}

                                id='reg-video-tournament'
                                label={intl.formatMessage({
                                    id: 'app.videos.edit.form.tournament.label',
                                    defaultMessage: 'Tournament'
                                })}
                                placeholder={intl.formatMessage({
                                    id: 'app.videos.edit.form.tournament.placeholder',
                                    defaultMessage: 'Enter Tournament'
                                })}
                                existingOptions={autocompleteOptions?.tournaments || []}
                                value={tournament}
                                onChange={handleChangeTournament}
                            />
                            <FormHelperText variant="outlined" error={!tournament} id="reg-video-tournament-help">
                                {showIfTrue(intl.formatMessage({
                                    id: 'app.videos.edit.form.tournament.help',
                                    defaultMessage: 'Select or enter new tournament'
                                }), !tournament)}
                            </FormHelperText>
                        </FormControl>

                        <div className={classes.row}>
                            {/* Year */}
                            <FormControl className={getClassInBlurMode(classes.formControl, !association)}>
                                <InputLabel className={classes.dropdownLabel} variant="outlined" required
                                            id="reg-video-year-label">
                                    {intl.formatMessage({
                                        id: 'app.videos.edit.form.year.label',
                                        defaultMessage: 'Year'
                                    })}
                                </InputLabel>
                                <Select id="reg-video-year-input" labelId="reg-video-year-label"
                                        variant="outlined"
                                        aria-describedby="reg-video-year-help"
                                        value={year} onChange={handleChangeYear}
                                        disabled={!association}>
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>

                                    {getYearsSince1990().map((y) =>
                                        <MenuItem key={y} value={y}>
                                            {y}
                                        </MenuItem>
                                    )}

                                </Select>
                                <FormHelperText variant="outlined" error={!year}
                                                id="reg-video-year-help">
                                    {showIfTrue(intl.formatMessage({
                                        id: 'app.videos.edit.form.year.help',
                                        defaultMessage: 'Enter year'
                                    }), !year)}
                                </FormHelperText>
                            </FormControl>

                            {/* Age group */}
                            <FormControl className={getClassInBlurMode(classes.formControl, !association)}>
                                <InputLabel className={classes.dropdownLabel} variant="outlined" required
                                            id="reg-video-age-label">
                                    {intl.formatMessage({
                                        id: 'app.videos.edit.form.ageGroup.label',
                                        defaultMessage: 'Age Group'
                                    })}
                                </InputLabel>
                                <Select id="reg-video-age-input" labelId="reg-video-age-label"
                                        variant="outlined"
                                        aria-describedby="reg-video-age-help"
                                        value={ageGroup} onChange={handleChangeAgeGroup}
                                        disabled={!association}>
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>

                                    {allowedAssociationStructure[association]?.ageGroups.map((a) =>
                                        <MenuItem key={a} value={a}>
                                            {intl.formatMessage({
                                                id: `app.ids.ageGroup.${a}`
                                            })}
                                        </MenuItem>
                                    )}

                                </Select>

                                <FormHelperText variant="outlined" error={!ageGroup} id="reg-video-age-help">
                                    {showIfTrue(intl.formatMessage({
                                        id: 'app.videos.edit.form.ageGroup.help',
                                        defaultMessage: 'Enter age group'
                                    }), !ageGroup)}
                                </FormHelperText>
                            </FormControl>
                        </div>
                    </div>

                    <div className={classes.row}>
                        {/* Athlete 1 */}
                        <FormControl className={getClassInBlurMode(classes.formControl, !association)}>
                            <SuggestionTextField
                                id='reg-video-surnameAthlete1'
                                label={intl.formatMessage({
                                    id: 'app.videos.edit.form.surnameAthlete1.label',
                                    defaultMessage: 'Athlete 1'
                                })}
                                placeholder={intl.formatMessage({
                                    id: 'app.videos.edit.form.surnameAthlete1.placeholder',
                                    defaultMessage: 'Enter Athlete 1'
                                })}
                                existingOptions={autocompleteOptions?.athletes || []}
                                value={surnameAthlete1}
                                onChange={handleChangeSurnameAthlete1}
                                disabled={!association}
                            />
                            <FormHelperText variant="outlined" error={!surnameAthlete1}
                                            id="reg-video-surnameAthlete1-help">
                                {showIfTrue(intl.formatMessage({
                                    id: 'app.videos.edit.form.surnameAthlete1.help',
                                    defaultMessage: 'Enter surname athlete 1'
                                }), !surnameAthlete1)}
                            </FormHelperText>
                        </FormControl>

                        {/* Athlete 2 */}
                        <FormControl className={getClassInBlurMode(classes.formControl, !association)}>
                            <SuggestionTextField
                                id='reg-video-surnameAthlete2'
                                label={intl.formatMessage({
                                    id: 'app.videos.edit.form.surnameAthlete2.label',
                                    defaultMessage: 'Athlete 2'
                                })}
                                placeholder={intl.formatMessage({
                                    id: 'app.videos.edit.form.surnameAthlete2.placeholder',
                                    defaultMessage: 'Enter Athlete 2'
                                })}
                                existingOptions={autocompleteOptions?.athletes || []}
                                value={surnameAthlete2}
                                onChange={handleChangeSurnameAthlete2}
                                disabled={!association}
                            />
                            <FormHelperText variant="outlined" error={!surnameAthlete2}
                                            id="reg-video-surnameAthlete2-help">
                                {showIfTrue(intl.formatMessage({
                                    id: 'app.videos.edit.form.surnameAthlete2.help',
                                    defaultMessage: 'Enter surname athlete 2'
                                }), !surnameAthlete2)}
                            </FormHelperText>
                        </FormControl>

                        {/* Gender */}
                        <FormControl className={getClassInBlurMode(classes.formControl, !association)}>
                            <InputLabel className={classes.dropdownLabel} variant="outlined" required
                                        id="reg-video-gender-label">
                                {intl.formatMessage({
                                    id: 'app.videos.edit.form.gender.label',
                                    defaultMessage: 'Gender'
                                })}
                            </InputLabel>
                            <Select id="reg-video-gender-input" labelId="reg-video-gender-label"
                                    variant="outlined"
                                    aria-describedby="reg-video-gender-help"
                                    value={gender} onChange={handleChangeGender}
                                    disabled={!association}>
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>

                                {getGenders().map((g) =>
                                    <MenuItem key={g} value={g}>
                                        {intl.formatMessage({
                                            id: `app.ids.gender.${g}`
                                        })}
                                    </MenuItem>
                                )}

                            </Select>
                            <FormHelperText variant="outlined" error={!gender}
                                            id="reg-video-gender-help">
                                {showIfTrue(intl.formatMessage({
                                    id: 'app.videos.edit.form.gender.help',
                                    defaultMessage: 'Enter gender'
                                }), !gender)}
                            </FormHelperText>
                        </FormControl>
                    </div>

                    <div>
                        {/* Info section in case the scores are not matching the selected video */}
                        {!scoreDefinitionMatchingWithSelectedDiscipline() && discipline && <div
                            className={classes.errorContainer}>
                            <Warning className={classes.warningIcon}/>
                            <div>
                                <div>
                                    {intl.formatMessage({
                                        id: 'app.videos.edit.form.discipline.notCompatibleWithExistingScenes.error',
                                        defaultMessage: 'Invalid scores for discipline {discipline}.'
                                    }, {
                                        discipline: intl.formatMessage({
                                            id: `app.ids.discipline.${discipline}`
                                        })
                                    })}
                                </div>
                                <div>
                                    {intl.formatMessage({
                                        id: 'app.videos.edit.form.discipline.notCompatibleWithExistingScenes.instruction',
                                        defaultMessage: 'Clean-up the scores first.'
                                    })}
                                </div>
                            </div>
                        </div>}

                        {/* Info if video data with dependencies for the scored scenes definitions have changed -
                        might requires manual changes by the admin. NO automatic statistical invalidation of scored scenes
                        will be done on video change. */}
                        {scoreDefinitionMatchingWithSelectedDiscipline() && scoringRelevantDataChanged() && discipline &&
                        <div
                            className={classes.warningContainer}>
                            <Warning className={classes.warningIcon}/>
                            <div>
                                <div>
                                    {intl.formatMessage({
                                        id: 'app.videos.edit.form.scoreRelevantData.changed.warning',
                                        defaultMessage: 'Data has been changed, which might leads to incompatible score definitions in the related scored scenes.'
                                    })}
                                </div>
                                <div>
                                    {intl.formatMessage({
                                        id: 'app.videos.edit.form.scoreRelevantData.changed.instruction',
                                        defaultMessage: 'Manual review of the related scored scenes required.'
                                    })}
                                </div>
                            </div>
                        </div>}

                        {/* Info if athlete 1 and athlete 2 are the same */}
                        {surnameAthlete1 && surnameAthlete2 && (surnameAthlete1 === surnameAthlete2) && <div
                            className={classes.errorContainer}>
                            <Warning className={classes.warningIcon}/>
                            <div>
                                <div>
                                    {intl.formatMessage({
                                        id: 'app.videos.edit.form.surnameAthlete2.sameAthlete.error',
                                        defaultMessage: 'Athlete {sameAthleteSurname} must not fight alone ;).'
                                    }, {sameAthleteSurname: surnameAthlete1})}
                                </div>
                                <div>
                                    {intl.formatMessage({
                                        id: 'app.videos.edit.form.surnameAthlete2.sameAthlete.instruction',
                                        defaultMessage: 'Select the opponent of {sameAthleteSurname}.'
                                    }, {sameAthleteSurname: surnameAthlete1})}
                                </div>
                            </div>
                        </div>}
                    </div>
                </div>
            </div>
        </fieldset>
    );
}
export default GeneralEditVideoForm
